import React, { createContext, useContext, type ReactNode } from 'react';
import { getCloudId } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useHelpCenterAri } from '../hooks/use-help-center-ari';
import type { helpCenterTypeContextQuery } from './__generated__/helpCenterTypeContextQuery.graphql';

export type HelpCenterType = 'BASIC' | 'HELP_HUB' | 'ADVANCED' | 'CUSTOMER_SERVICE' | 'UNIFIED' | null;

interface HelpCenterTypeContextValue {
    helpCenterType: HelpCenterType;
    isHelpHub: boolean;
}

const HelpCenterTypeContext = createContext<HelpCenterTypeContextValue | undefined>(undefined);

interface HelpCenterTypeProviderProps {
    children: ReactNode;
}

export const HelpCenterTypeProvider: React.FC<HelpCenterTypeProviderProps> = ({ children }) => {
    di(useLazyLoadQuery, getCloudId, useHelpCenterAri);
    const helpCenterAri = useHelpCenterAri();
    const cloudId = getCloudId();

    const data = useLazyLoadQuery<helpCenterTypeContextQuery>(
        graphql`
            query helpCenterTypeContextQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
                    helpCenterById(helpCenterAri: $helpCenterAri) {
                        ... on HelpCenter {
                            type @optIn(to: "HelpCenterTypeTest")
                        }
                    }
                }
            }
        `,
        { cloudId, helpCenterAri },
        {
            fetchPolicy: 'store-or-network',
        }
    );

    const helpCenterById = data?.helpCenter?.helpCenterById;
    const helpCenterType: HelpCenterType = (helpCenterById?.type as HelpCenterType) ?? null;

    const isHelpHub = helpCenterType === 'HELP_HUB';

    const value: HelpCenterTypeContextValue = {
        helpCenterType,
        isHelpHub,
    };

    return <HelpCenterTypeContext.Provider value={value}>{children}</HelpCenterTypeContext.Provider>;
};

export const useHelpCenterType = (): HelpCenterTypeContextValue => {
    const context = useContext(HelpCenterTypeContext);
    if (context === undefined) {
        throw new Error('useHelpCenterType must be used within a HelpCenterTypeProvider');
    }
    return context;
};

/**
 * Optional version of useHelpCenterType that returns a default value if not within a provider.
 * Use this in components that can be rendered both inside and outside the HelpCenterTypeProvider,
 * such as HelpCenterForm which is used for both creating (no context) and editing (has context).
 */
export const useOptionalHelpCenterType = (): HelpCenterTypeContextValue => {
    const context = useContext(HelpCenterTypeContext);
    // Default to BASIC/Help Center when context is not available (e.g., creating new help centers)
    return context ?? { helpCenterType: null, isHelpHub: false };
};
